'use client';
import { useState } from 'react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

const FILE_TYPE_OPTIONS = [
  { value: 'txt', label: 'Texto (.txt)', icon: '📄' },
  { value: 'csv', label: 'CSV (.csv)', icon: '📊' },
  { value: 'xlsx', label: 'Excel (.xlsx, .xls)', icon: '📗' },
  { value: 'pdf', label: 'PDF (.pdf)', icon: '📕' },
  { value: 'docx', label: 'Word (.docx, .doc)', icon: '📘' },
  { value: 'pptx', label: 'PowerPoint (.pptx, .ppt)', icon: '📙' },
  { value: 'img', label: 'Imagen (.jpg, .jpeg, .png, .gif, .svg)', icon: '🖼️' },
];

export default function FileUploadEdit({
  question,
  setQuestion,
  errorMessage,
  helperText,
}) {

  const { enqueueSnackbar } = useSnackbar();

  // Extraer configuración actual
  const allowedTypes =
  question.customOptions?.map(opt =>
    typeof opt === 'string'
      ? opt.toLowerCase().trim()
      : opt?.value?.toLowerCase().trim()
  ).filter(Boolean) ?? ['pdf'];
  const maxFileSize = question.stars.length ?? question.stars ??  5; // en MB
  const maxFiles = question.limitOptions ?? question.maxFile ?? 1;

  const handleTypeToggle = (typeValue) => {
    const currentTypes = question.customOptions || [];
    const exists = currentTypes.includes(typeValue);

    let newTypes;
    if (exists) {
      // Remover el tipo
      newTypes = currentTypes.filter((type) => type !== typeValue);
    } else {
      // Agregar el tipo
      newTypes = [...currentTypes, typeValue];
    }

    // Validar que al menos haya un tipo seleccionado
    if (newTypes.length === 0) {
      enqueueSnackbar('Debes seleccionar al menos un tipo de archivo', {
        variant: 'warning',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      return;
    }

    setQuestion((prev) => ({
      ...prev,
      customOptions: newTypes,
    }));
  };

  const handleMaxFileSizeChange = (e) => {
    const value = parseInt(e.target.value) || 1;

    // Limitar entre 1 y 5 MB
    const limitedValue = Math.max(1, Math.min(5, value));

    setQuestion((prev) => ({
      ...prev,
      stars: limitedValue,
    }));
  };

  const handleMaxFilesChange = (e) => {
    const value = parseInt(e.target.value) || 1;

    // Limitar entre 1 y 5 archivos
    const limitedValue = Math.max(1, Math.min(5, value));

    setQuestion((prev) => ({
      ...prev,
      limitOptions: limitedValue,
    }));
  };

  const isTypeSelected = (typeValue) => {
    return allowedTypes.includes(typeValue);
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      {errorMessage?.fileUpload && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {helperText?.fileUpload || 'Error en configuración de archivos'}
        </Alert>
      )}

      <Card elevation={3} sx={{ borderRadius: 3, overflow: 'visible' }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={4}>
            {/* Tipos de Archivo Permitidos */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                border: errorMessage?.customOptions ? '2px solid #d32f2f' : 'none',
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}
              >
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    p: 1,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <InsertDriveFileIcon />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Tipos de Archivo Permitidos *
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Selecciona los formatos que los usuarios pueden subir
                  </Typography>
                </Box>
              </Box>

              <FormControl
                component="fieldset"
                error={!!errorMessage?.customOptions}
                sx={{ width: '100%' }}
              >
                <FormGroup>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                      },
                      gap: 2,
                    }}
                  >
                    {FILE_TYPE_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Switch
                            checked={isTypeSelected(option.value)}
                            onChange={() => handleTypeToggle(option.value)}
                            color="primary"
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <span style={{ fontSize: '1.2rem' }}>
                              {option.icon}
                            </span>
                            <Typography variant="body2">
                              {option.label}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          border: '1px solid',
                          borderColor: isTypeSelected(option.value)
                            ? 'primary.main'
                            : 'grey.300',
                          borderRadius: 2,
                          p: 1.5,
                          m: 0,
                          bgcolor: isTypeSelected(option.value)
                            ? 'primary.50'
                            : 'white',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'primary.50',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </FormGroup>
                {errorMessage?.customOptions && (
                  <FormHelperText sx={{ mt: 1, fontSize: '0.875rem' }}>
                    {helperText?.customOptions ||
                      'Debes seleccionar al menos un tipo de archivo'}
                  </FormHelperText>
                )}
              </FormControl>

              {/* Mostrar tipos seleccionados */}
              {allowedTypes.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: 'block' }}
                  >
                    Tipos seleccionados ({allowedTypes.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {allowedTypes.map((type) => {
                      const option = FILE_TYPE_OPTIONS.find(
                        (opt) => opt.value === type
                      );

                      return (
                        <Chip
                          key={type}
                          label={option?.label ?? type.toUpperCase()}
                          onDelete={() => handleTypeToggle(type)}
                          deleteIcon={<CloseIcon />}
                          color="primary"
                          variant="filled"
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Configuración de Tamaño y Cantidad */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}
              >
                <Box
                  sx={{
                    bgcolor: 'secondary.main',
                    color: 'white',
                    p: 1,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CloudUploadIcon />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Límites de Carga
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Define el tamaño máximo y cantidad de archivos
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 3,
                }}
              >
                {/* Tamaño máximo */}
                <TextField
                  label="Tamaño máximo por archivo (MB)"
                  type="number"
                  value={maxFileSize}
                  onChange={handleMaxFileSizeChange}
                  error={!!errorMessage?.stars}
                  helperText={helperText?.stars || 'Límite: 1-5 MB'}
                  InputProps={{
                    inputProps: {
                      min: 1,
                      max: 5,
                    },
                  }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />

                {/* Cantidad máxima */}
                <TextField
                  label="Cantidad máxima de archivos"
                  type="number"
                  value={maxFiles}
                  onChange={handleMaxFilesChange}
                  error={!!errorMessage?.limitOptions}
                  helperText={
                    helperText?.limitOptions || 'Límite: 1-5 archivos'
                  }
                  InputProps={{
                    inputProps: {
                      min: 1,
                      max: 5,
                    },
                  }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>
            </Paper>

            {/* Resumen de Configuración */}
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Configuración actual:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                • Los usuarios podrán subir hasta <strong>{maxFiles}</strong>{' '}
                {maxFiles === 1 ? 'archivo' : 'archivos'}
              </Typography>
              <Typography variant="body2">
                • Tamaño máximo por archivo: <strong>{maxFileSize} MB</strong>
              </Typography>
              <Typography variant="body2">
                • Formatos permitidos: <strong>{allowedTypes.length}</strong>{' '}
                {allowedTypes.length === 1 ? 'tipo' : 'tipos'}
              </Typography>
            </Alert>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}